import type { LinkInfo } from "../api/extensionLinks.js";
import { b64ToBytes, bytesToB64 } from "../b64.js";
import { normalizeServerUrl } from "./serverUrl.js";

/**
 * Extension link (roadmap step 27): the extension opens /link-extension#<id>.<publicKeyB64url>.
 * The fragment never goes to the server. It is read once at page load, kept in sessionStorage so
 * it survives the sign-in, and removed from the address bar right away.
 */
export const LINK_PATH = "/link-extension";
/** The server lets a request live 2 minutes; the stash outlives that only to explain the expiry. */
export const LINK_STASH_TTL_MS = 10 * 60_000;
const STASH_KEY = "pf.linkRequest";
const ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;
const KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const FRAGMENT_PATTERN = /^#([A-Za-z0-9_-]{22})\.([A-Za-z0-9_-]{43})$/;

export interface LinkRequest {
  /** Request id as the extension sent it (base64url, 16 bytes). */
  readonly id: string;
  /** The extension's one-time X25519 key, standard base64. */
  readonly publicKeyB64: string;
  readonly storedAt: number;
}

export interface LinkStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LinkWindow {
  readonly location: { readonly pathname: string; readonly search: string; readonly hash: string };
  readonly history: { replaceState: (data: unknown, unused: string, url: string) => void };
}

/** sessionStorage, or nothing when the browser blocks it (then the link page has no request). */
export function linkStorage(): LinkStorage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Strict base64url without padding; undefined unless it re-encodes to the same text. */
function b64UrlToBytes(text: string, length: number): Uint8Array | undefined {
  let bytes: Uint8Array;
  try {
    bytes = b64ToBytes(text.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (text.length % 4)) % 4));
  } catch {
    return undefined;
  }
  if (bytes.length !== length) {
    return undefined;
  }
  const again = bytesToB64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return again === text ? bytes : undefined;
}

/** The request id as standard base64, for the crypto worker. */
export function linkIdB64(id: string): string | undefined {
  if (!ID_PATTERN.test(id)) {
    return undefined;
  }
  const bytes = b64UrlToBytes(id, 16);
  return bytes === undefined ? undefined : bytesToB64(bytes);
}

function parseFragment(hash: string, now: number): LinkRequest | undefined {
  const match = FRAGMENT_PATTERN.exec(hash);
  const id = match?.[1];
  const keyText = match?.[2];
  if (id === undefined || keyText === undefined || !KEY_PATTERN.test(keyText) || linkIdB64(id) === undefined) {
    return undefined;
  }
  const key = b64UrlToBytes(keyText, 32);
  return key === undefined ? undefined : { id, publicKeyB64: bytesToB64(key), storedAt: now };
}

/**
 * Runs once before the app renders. On the link page it moves a well-formed fragment into
 * session storage and strips the fragment from the URL (no history or referrer keeps it).
 */
export function captureLinkFragment(win: LinkWindow, storage: LinkStorage | undefined, now: number): boolean {
  if (win.location.pathname !== LINK_PATH || win.location.hash === "") {
    return false;
  }
  const request = parseFragment(win.location.hash, now);
  win.history.replaceState(null, "", `${LINK_PATH}${win.location.search}`);
  // Without session storage the request is lost, but the fragment still leaves the URL.
  if (request === undefined || storage === undefined) {
    return false;
  }
  storage.setItem(STASH_KEY, JSON.stringify(request));
  return true;
}

/** The stashed request, unless it is malformed or too old (then it is removed). */
export function readLinkRequest(storage: LinkStorage, now: number): LinkRequest | undefined {
  const raw = storage.getItem(STASH_KEY);
  if (raw === null) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = undefined;
  }
  if (isRecord(parsed) &&
    typeof parsed["id"] === "string" &&
    typeof parsed["publicKeyB64"] === "string" &&
    typeof parsed["storedAt"] === "number" &&
    linkIdB64(parsed["id"]) !== undefined &&
    now - parsed["storedAt"] <= LINK_STASH_TTL_MS) {
    return { id: parsed["id"], publicKeyB64: parsed["publicKeyB64"], storedAt: parsed["storedAt"] };
  }
  storage.removeItem(STASH_KEY);
  return undefined;
}

export function dropLinkRequest(storage: LinkStorage): void {
  storage.removeItem(STASH_KEY);
}

/** True when both are the same 32 key bytes. The server must not swap the fragment key. */
export function sameLinkKey(fragmentKeyB64: string, serverKeyB64: string): boolean {
  let a: Uint8Array;
  let b: Uint8Array;
  try {
    a = b64ToBytes(fragmentKeyB64);
    b = b64ToBytes(serverKeyB64);
  } catch {
    return false;
  }
  return a.length === 32 && b.length === 32 && a.every((byte: number, i: number): boolean => byte === b[i]);
}

/** Six digits as typed ("123 456" is fine); undefined otherwise. */
export function normalizeLinkCode(input: string): string | undefined {
  const digits = input.replace(/\s+/g, "");
  return /^\d{6}$/.test(digits) ? digits : undefined;
}

/**
 * The server address the extension must have configured: the API base when the build sets one,
 * else this page's origin (API and web app share it). Normalized like the extension does.
 */
export function linkServerUrl(apiBase: string, origin: string): string | undefined {
  return normalizeServerUrl(apiBase !== "" ? apiBase : origin);
}

/** "1:05" for the time left; never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export type LinkPhase =
  | { readonly kind: "missing" }
  | { readonly kind: "loading" }
  | { readonly kind: "review"; readonly info: LinkInfo; readonly error: string | undefined }
  | { readonly kind: "approving"; readonly info: LinkInfo }
  | { readonly kind: "sealing"; readonly info: LinkInfo }
  | { readonly kind: "done"; readonly info: LinkInfo }
  | { readonly kind: "failed"; readonly message: string };

export type LinkEvent =
  | { readonly type: "loaded"; readonly info: LinkInfo }
  | { readonly type: "submit" }
  /** Approve failed but the request is still open (wrong code, step-up cancelled, network). */
  | { readonly type: "retry"; readonly message: string }
  | { readonly type: "approved" }
  | { readonly type: "delivered" }
  | { readonly type: "rejected"; readonly message: string }
  | { readonly type: "expired" };

export const LINK_NO_STORAGE_MESSAGE = "Dein Browser blockiert den Sitzungsspeicher für diese Seite, deshalb kann die Anfrage nicht übernommen werden. Erlaube Website-Daten für diese Seite und starte „Mit Passkey anmelden“ in der Erweiterung neu.";

export const LINK_EXPIRED_MESSAGE = "Die Anfrage ist abgelaufen. Starte „Mit Passkey anmelden“ in der Erweiterung neu.";

export function linkReducer(state: LinkPhase, event: LinkEvent): LinkPhase {
  if (state.kind === "done" || state.kind === "failed") {
    return state;
  }
  switch (event.type) {
    case "loaded":
      return state.kind === "loading" ? { kind: "review", info: event.info, error: undefined } : state;
    case "submit":
      return state.kind === "review" ? { kind: "approving", info: state.info } : state;
    case "retry":
      return state.kind === "approving" ? { kind: "review", info: state.info, error: event.message } : state;
    case "approved":
      return state.kind === "approving" ? { kind: "sealing", info: state.info } : state;
    case "delivered":
      return state.kind === "sealing" ? { kind: "done", info: state.info } : state;
    case "rejected":
      return { kind: "failed", message: event.message };
    case "expired":
      // Once approved, the hand-over is already under way; the server decides.
      return state.kind === "sealing" ? state : { kind: "failed", message: LINK_EXPIRED_MESSAGE };
  }
}

/** Approve errors: 400 (wrong code, attempts left), 429 and network errors can be retried; 403/404/409 end the request. */
export function approveFailure(status: number, code: string | undefined, message: string): LinkEvent {
  if (status === 403 && code === "PASSKEY_REAUTH_REQUIRED") {
    return { type: "retry", message: "Ohne Bestätigung mit deinem Passkey kann die Erweiterung nicht verbunden werden." };
  }
  if (status === 404) {
    return { type: "rejected", message: LINK_EXPIRED_MESSAGE };
  }
  if (status === 403 || status === 409) {
    return { type: "rejected", message };
  }
  return { type: "retry", message };
}
